import { ComponentType, lazy } from "react";

const RELOADED_KEY = "medecine360.pageLoadReloaded";

/**
 * A page's code could not be downloaded. Only this error is handled by
 * PageLoadBoundary: a page that crashes while rendering keeps its behaviour.
 */
export class PageLoadError extends Error {}

const readFlag = () => {
  try {
    return sessionStorage.getItem(RELOADED_KEY) !== null;
  } catch {
    return true; // No storage: never risk a reload loop
  }
};

const writeFlag = (set: boolean) => {
  try {
    if (set) sessionStorage.setItem(RELOADED_KEY, "1");
    else sessionStorage.removeItem(RELOADED_KEY);
  } catch {
    // The flag only guards the automatic reload
  }
};

/**
 * React.lazy for a route (MED-1743). A tab opened before a deployment asks for
 * chunks that no longer exist: reload once to get the new index.html. Offline,
 * or if the reload did not help, let PageLoadBoundary show its message.
 */
export const lazyPage = <M, K extends keyof M>(
  load: () => Promise<M>,
  exportName: K
) =>
  lazy(async () => {
    try {
      const loaded = await load();
      writeFlag(false);
      return { default: loaded[exportName] as ComponentType };
    } catch (error) {
      if (navigator.onLine !== false && !readFlag()) {
        writeFlag(true);
        window.location.reload();
        return new Promise<never>(() => {}); // Keep the loader until the reload
      }
      throw new PageLoadError(
        error instanceof Error ? error.message : "Page load failed"
      );
    }
  });
